import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PaperAirplaneIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Card, Button, Badge, Table, Modal, ModalFooter, Input, Select } from '@/components/common';
import {
  useWebhooks,
  useCreateWebhook,
  useUpdateWebhook,
  useDeleteWebhook,
  useWebhookDeliveries,
  useTestWebhook,
  useResendWebhookDelivery,
} from '@/hooks/useIntegrations';
import { formatDateTime } from '@/utils';

const EVENT_TYPE_OPTIONS = [
  { value: 'quote_completed', label: 'Quote Completed' },
  { value: 'policy_issued', label: 'Policy Issued' },
  { value: 'kyc_status_changed', label: 'KYC Status Changed' },
  { value: 'break_in_status_changed', label: 'Break-In Status Changed' },
  { value: 'batch_completed', label: 'Batch Completed' },
];

function eventTypeLabel(value: string): string {
  return EVENT_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

interface Webhook {
  id: string;
  event_type: string;
  callback_url: string;
  is_active: boolean;
  last_triggered_at: string | null;
  failure_count: number;
  disabled_reason?: string;
}

interface Delivery {
  id: string;
  event_type: string;
  is_test: boolean;
  status: 'pending' | 'delivered' | 'failed';
  attempt_count: number;
  next_attempt_at: string;
  last_status_code: number | null;
  last_error: string;
  created_at: string;
}

interface WebhookFormData {
  event_type: string;
  callback_url: string;
  secret_key: string;
}

function WebhookFormModal({
  isOpen,
  onClose,
  initialData,
  webhookId,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Partial<WebhookFormData & { event_type: string }>;
  webhookId?: string;
}) {
  const createMutation = useCreateWebhook();
  const updateMutation = useUpdateWebhook();
  const isEdit = !!webhookId;

  const { register, handleSubmit, reset, formState: { errors } } = useForm<WebhookFormData>({
    defaultValues: {
      event_type: initialData?.event_type ?? '',
      callback_url: initialData?.callback_url ?? '',
      secret_key: '',
    },
  });

  const onSubmit = (data: WebhookFormData) => {
    if (isEdit) {
      updateMutation.mutate(
        { id: webhookId, data: { ...data, secret_key: data.secret_key || undefined } },
        { onSuccess: () => { reset(); onClose(); } }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => { reset(); onClose(); },
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Webhook' : 'Add Webhook'}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <Select
            label="Event Type"
            options={EVENT_TYPE_OPTIONS}
            placeholder="Select event type..."
            required
            error={errors.event_type?.message}
            {...register('event_type', { required: 'Event type is required' })}
          />
          <Input
            label="Callback URL"
            type="url"
            placeholder="https://your-server.com/webhook (https only)"
            required
            error={errors.callback_url?.message}
            {...register('callback_url', { required: 'Callback URL is required' })}
          />
          <Input
            label={isEdit ? 'Secret Key (leave blank to keep current)' : 'Secret Key'}
            type="password"
            placeholder="••••••••••••"
            required={!isEdit}
            error={errors.secret_key?.message}
            helperText="Used to sign every request (X-PolicyOnline-Signature: t=..,v1=HMAC-SHA256 of t.body). Reject timestamps older than 5 minutes."
            {...register('secret_key', {
              required: isEdit ? false : 'Secret key is required',
            })}
          />
        </div>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isPending}>
            {isEdit ? 'Save Changes' : 'Create Webhook'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function DeliveryLogModal({ webhook, onClose }: { webhook: Webhook; onClose: () => void }) {
  const { data, isLoading, isError } = useWebhookDeliveries(webhook.id);
  const resend = useResendWebhookDelivery(webhook.id);
  const rows: Delivery[] = (data as unknown as { results?: Delivery[] })?.results ?? [];
  const variant = (s: Delivery['status']) => (s === 'delivered' ? 'success' : s === 'failed' ? 'error' : 'warning');

  return (
    <Modal isOpen onClose={onClose} title={`Delivery log: ${eventTypeLabel(webhook.event_type)}`} size="lg">
      {isError ? (
        <p className="text-sm text-red-600 dark:text-red-400">The delivery log could not be loaded. Try again.</p>
      ) : isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nothing has been sent to this webhook yet. Use Send test to try it.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-3">When</th>
                <th className="py-1 pr-3">Status</th>
                <th className="py-1 pr-3">Tries</th>
                <th className="py-1 pr-3">Answer</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700 align-top">
                  <td className="py-1 pr-3">
                    {formatDateTime(r.created_at)}
                    {r.is_test && <span className="ml-1 text-xs text-gray-400">(test)</span>}
                  </td>
                  <td className="py-1 pr-3">
                    <Badge variant={variant(r.status)} size="sm">{r.status}</Badge>
                    {r.status === 'pending' && (
                      <div className="text-xs text-gray-400">next {formatDateTime(r.next_attempt_at)}</div>
                    )}
                  </td>
                  <td className="py-1 pr-3">{r.attempt_count}</td>
                  <td className="py-1 pr-3">
                    {r.last_status_code ? `HTTP ${r.last_status_code}` : ''} {r.last_error}
                  </td>
                  <td className="py-1">
                    {!r.is_test && r.status !== 'pending' && (
                      <Button size="sm" variant="outline" isLoading={resend.isPending} onClick={() => resend.mutate(r.id)}>
                        Resend
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}

function DeleteConfirmModal({
  isOpen,
  onClose,
  webhookId,
  eventType,
}: {
  isOpen: boolean;
  onClose: () => void;
  webhookId: string;
  eventType: string;
}) {
  const deleteMutation = useDeleteWebhook();

  const handleDelete = () => {
    deleteMutation.mutate(webhookId, { onSuccess: onClose });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Webhook" size="sm">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Are you sure you want to delete the <span className="font-medium">{eventTypeLabel(eventType)}</span> webhook?
        This action cannot be undone.
      </p>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" isLoading={deleteMutation.isPending} onClick={handleDelete}>
          Delete
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function WebhooksPage() {
  const { data, isLoading } = useWebhooks();
  const updateMutation = useUpdateWebhook();
  const testMutation = useTestWebhook();

  const [logWebhook, setLogWebhook] = useState<Webhook | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editWebhook, setEditWebhook] = useState<Webhook | null>(null);
  const [deleteWebhook, setDeleteWebhook] = useState<Webhook | null>(null);

  const webhooks: Webhook[] = (data as unknown as { results?: Webhook[] })?.results ?? (Array.isArray(data) ? (data as unknown as Webhook[]) : []);

  const handleToggleActive = (webhook: Webhook) => {
    updateMutation.mutate({ id: webhook.id, data: { is_active: !webhook.is_active } });
  };

  const columns = [
    {
      key: 'event_type',
      header: 'Event Type',
      render: (item: Webhook) => (
        <Badge variant="info" size="sm">
          {eventTypeLabel(item.event_type)}
        </Badge>
      ),
    },
    {
      key: 'callback_url',
      header: 'Callback URL',
      render: (item: Webhook) => (
        <span className="font-mono text-xs text-gray-700 dark:text-gray-300 max-w-xs truncate block" title={item.callback_url}>
          {item.callback_url}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Active',
      render: (item: Webhook) => (
        <button
          type="button"
          onClick={() => handleToggleActive(item)}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            item.is_active ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
          }`}
          aria-label={item.is_active ? 'Deactivate' : 'Activate'}
          title={item.disabled_reason || undefined}
        >
          <span
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              item.is_active ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </button>
      ),
    },
    {
      key: 'last_triggered_at',
      header: 'Last Triggered',
      render: (item: Webhook) =>
        item.last_triggered_at
          ? formatDateTime(item.last_triggered_at)
          : <span className="text-gray-400">Never</span>,
    },
    {
      key: 'failure_count',
      header: 'Failures',
      render: (item: Webhook) => (
        <span className={item.failure_count > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
          {item.failure_count}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (item: Webhook) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => testMutation.mutate(item.id)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Send test event"
            title="Send test event"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setLogWebhook(item)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Delivery log"
            title="Delivery log"
          >
            <ClipboardDocumentListIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setEditWebhook(item)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Edit webhook"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setDeleteWebhook(item)}
            className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            aria-label="Delete webhook"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Webhooks"
        subtitle="Receive real-time event notifications from your insurer integrations"
        breadcrumbs={[{ label: 'Webhooks' }]}
        actions={
          <Button
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={() => setShowForm(true)}
          >
            Add Webhook
          </Button>
        }
      />

      <Card padding="none">
        <Table<Webhook>
          data={webhooks}
          columns={columns}
          isLoading={isLoading}
          rowKey={(item) => item.id}
          emptyMessage="No webhooks configured. Add your first webhook to receive event notifications."
        />
      </Card>

      {/* Add webhook modal */}
      <WebhookFormModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
      />

      {/* Edit webhook modal */}
      {editWebhook && (
        <WebhookFormModal
          isOpen={!!editWebhook}
          onClose={() => setEditWebhook(null)}
          initialData={{ event_type: editWebhook.event_type, callback_url: editWebhook.callback_url }}
          webhookId={editWebhook.id}
        />
      )}

      {logWebhook && <DeliveryLogModal webhook={logWebhook} onClose={() => setLogWebhook(null)} />}

      {/* Delete confirmation */}
      {deleteWebhook && (
        <DeleteConfirmModal
          isOpen={!!deleteWebhook}
          onClose={() => setDeleteWebhook(null)}
          webhookId={deleteWebhook.id}
          eventType={deleteWebhook.event_type}
        />
      )}
    </div>
  );
}
